import type { CSSProperties } from "react";
import type { LayerTransform } from "../types";

const COVER_STYLE: CSSProperties = {
  width: "100%",
  height: "100%",
  objectFit: "cover",
};

/**
 * Narrow an untyped layer prop to a usable rect. Layer props are jsonb, so
 * every rect arrives unvalidated; a rect with a missing field, a non-finite
 * number, or a non-positive size is treated as absent rather than rendered at
 * NaN coordinates.
 */
export function readRect(value: unknown): LayerTransform | null {
  if (!value || typeof value !== "object") return null;
  const { x, y, width, height } = value as Record<string, unknown>;
  if (
    typeof x !== "number" ||
    typeof y !== "number" ||
    typeof width !== "number" ||
    typeof height !== "number" ||
    !Number.isFinite(x) ||
    !Number.isFinite(y) ||
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    width <= 0 ||
    height <= 0
  ) {
    return null;
  }
  return { x, y, width, height };
}

/**
 * The window a `content` crop is measured against.
 *
 * Placement and framing are independent: `transform` isolates the clip to part
 * of the canvas, `content` crops whatever window it lands in. So a layer that
 * only crops gets the canvas as its window rather than falling back to an
 * uncropped render, and a full-bleed crop never has to restate the canvas rect.
 *
 * Null when the layer does neither, which is the uncropped full-canvas render
 * and needs no wrapper at all.
 */
export function resolveCropWindow(
  transform: unknown,
  content: unknown,
  canvas: { width: number; height: number },
): LayerTransform | null {
  const placed = readRect(transform);
  if (placed) return placed;
  if (!readRect(content)) return null;
  return { x: 0, y: 0, width: canvas.width, height: canvas.height };
}

/**
 * Style for the inner video element within its window wrapper.
 *
 * Without a crop (`content` absent) the video fills the window via
 * objectFit:cover — the historical uncropped behavior. With a `content` rect
 * the video is positioned at that rect, which is already relative to the
 * window's top-left, and clipped by the wrapper's overflow:hidden.
 * objectFit:fill is exact because the content rect already carries the video's
 * intrinsic aspect ratio, so panning (moving `content`) and zooming (scaling
 * `content`) render precisely.
 *
 * The max-width/max-height overrides matter whenever a composition renders
 * inline in the app DOM rather than in the sandboxed iframe: the app's
 * CSS-Remedy reset (`img, video { max-width: 100%; height: auto }`) clamps a
 * crop wider than its window down to the window, which shifts the framing and
 * leaves a gap. A `max-*` constraint wins over an inline width, so the geometry
 * has to opt out explicitly. Same hazard `AdCssIsolation` shields the static
 * ad canvas from.
 */
export function videoContentStyle(content: unknown): CSSProperties {
  const crop = readRect(content);
  if (!crop) return COVER_STYLE;
  return {
    position: "absolute",
    left: `${crop.x}px`,
    top: `${crop.y}px`,
    width: `${crop.width}px`,
    height: `${crop.height}px`,
    maxWidth: "none",
    maxHeight: "none",
    objectFit: "fill",
  };
}
